import { useQuery, useMutation, ApolloError } from '@apollo/client';
import { useState, useEffect, useRef, FC, useMemo } from 'react';

import {
  VideoImageSource,
  VideoSource,
  VideoV2Status,
} from '@xing-com/crate-common-graphql-types';
import { BodyCopy } from '@xing-com/typography';

import { CreateVideoTrackingEventDocument } from '../../graphql/mutations/create-video-tracking-event.gql-types';
import { VideoV2Document } from '../../graphql/queries/video-v2.gql-types';
import { Encoding } from '../encoding/encoding';
import { MessageContainer } from '../message-container/message-container';
import { Player } from '../player/player';
import type { Props as PlayerProps } from '../player/player';
import { PreviewMode } from '../preview-mode/preview-mode';

const getSources = (sources: VideoSource[]): string[] => {
  const sourceList = [];

  if (sources) {
    const hls = sources.find(
      (item) => item.format === 'application/x-mpegURL'
    )?.source;
    const dash = sources.find(
      (item) => item.format === 'application/dash+xml'
    )?.source;

    if (hls) sourceList.push(hls);
    if (dash) sourceList.push(dash);
  }

  return sourceList;
};

export type Props = Omit<PlayerProps, 'sources' | 'configuration'> & {
  previewModeAction?: (callback: () => void) => void;
  previewModeButtonSize?: number;
  encodingFallback?: FC<{ encodingTitle?: string }>;
  onError?: () => void;
  onEncodingComplete?: (data: {
    id: string;
    sources: string[];
    status: string | undefined;
    sprites: VideoImageSource[] | null | undefined;
    thumbnails: PlayerThumbnail[];
    urn: string | undefined;
    error: ApolloError | undefined;
  }) => void;
  encodingTitle?: string;
  configuration?: PlayerProps['configuration'];
};
export const PlayerContainer: FC<Props> = ({
  id,
  onError,
  onEncodingComplete,
  encodingFallback,
  previewModeAction,
  previewModeButtonSize,
  posterImage,
  encodingTitle,
  configuration = {},
  isStory = false,
  ...props
}) => {
  const [forceAutoplay, setForceAutoplay] = useState(false);
  const [previewMode, setPreviewMode] = useState(
    typeof previewModeAction !== 'undefined'
  );

  const prevStatus = useRef<
    VideoV2Status.Available | VideoV2Status.Failed | 'encoding' | undefined
  >();
  const updateVideoTimer = useRef<NodeJS.Timeout>();

  const { data, loading, error, refetch } = useQuery(VideoV2Document, {
    variables: { ref: id.toString() },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const [trackEventMutation] = useMutation(CreateVideoTrackingEventDocument);

  const globalId = data?.videoV2?.globalId;
  const videoStatus =
    data?.videoV2?.status === VideoV2Status.Created ||
    data?.videoV2?.status === VideoV2Status.Encoding
      ? 'encoding'
      : data?.videoV2?.status;

  const sources = data?.videoV2?.sources
    ? getSources(data?.videoV2?.sources)
    : undefined;
  const subtitles = data?.videoV2?.subtitles ?? undefined;
  const sprites = data?.videoV2?.sprites?.[0]?.sources;

  const customThumbnails = data?.videoV2?.customThumbnails;
  const defaultThumbnails = data?.videoV2?.defaultThumbnails;

  const thumbnails: PlayerThumbnail[] = useMemo(() => {
    const thumbnailsData =
      (customThumbnails && customThumbnails.length > 0
        ? customThumbnails
        : defaultThumbnails) ?? [];

    return thumbnailsData.map((thumbnail) => ({
      sources: (thumbnail?.sources ?? []).reduce(
        (_: PlayerThumbnail['sources'], source) => {
          if (source) {
            _.push({
              height: source.height,
              width: source.width,
              source: source.source,
            });
          }
          return _;
        },
        []
      ),
    }));
  }, [customThumbnails, defaultThumbnails]);

  const hasError = (!loading && !data?.videoV2?.ref) || error;

  useEffect(() => {
    if (videoStatus === 'encoding') {
      updateVideoTimer.current = setInterval(() => refetch(), 5000);
    }

    if (
      prevStatus.current === 'encoding' &&
      videoStatus !== prevStatus.current
    ) {
      if (onEncodingComplete) {
        // returning an object thinking about future implementations using the status
        onEncodingComplete({
          id: id.toString(),
          sources: sources ?? [],
          status: videoStatus,
          sprites: sprites,
          thumbnails: thumbnails,
          urn: globalId,
          error: error,
        });
      }
    }

    prevStatus.current = videoStatus;

    return () => {
      if (updateVideoTimer?.current) {
        clearInterval(updateVideoTimer.current);
      }
    };
  }, [videoStatus]);

  useEffect(() => {
    if (hasError) {
      onError?.();
    }
  }, [hasError]);

  if (hasError) {
    return (
      <MessageContainer>
        <BodyCopy>Error loading video</BodyCopy>
      </MessageContainer>
    );
  }

  if (videoStatus === 'encoding') {
    const CustomEncoding = encodingFallback;
    return CustomEncoding ? (
      <CustomEncoding encodingTitle={encodingTitle} />
    ) : (
      <MessageContainer>
        <Encoding />
      </MessageContainer>
    );
  }

  if (loading || !sources || !sources.length) {
    return null;
  }

  if (previewMode) {
    return (
      <PreviewMode
        thumbnails={thumbnails}
        posterImage={posterImage}
        buttonSize={previewModeButtonSize}
        onClick={() =>
          previewModeAction?.(() => {
            setPreviewMode(false);
            setForceAutoplay(true);
          })
        }
      />
    );
  }

  return (
    <Player
      id={id}
      sources={sources}
      thumbnails={thumbnails?.[0]?.sources ?? undefined}
      sprites={sprites ?? undefined}
      subtitles={subtitles}
      forceAutoplay={forceAutoplay}
      trackEventMutation={trackEventMutation}
      configuration={configuration}
      isStory={isStory}
      {...props}
    />
  );
};
